import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { holidayRegionSchema } from './calendar/holidays.js';
import { emailSchema, userNameSchema, userTypeSchema } from './auth.js';
import { visibilitySchema } from './permissions.js';
import { richTextSchema } from './rich-text.js';

/** PROJ-01..05: projects, workflow (statuses), labels and members. */

// --- Common ------------------------------------------------------------------------------------

/** Business date `YYYY-MM-DD` (CLAUDE.md conventions). */
export const isoDateSchema = z.iso.date();

/** `#rrggbb`, stored lowercase. */
export const colorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/)
  .transform((c) => c.toLowerCase());

/** Suggested colors for statuses and labels; any `#rrggbb` is accepted. */
export const colorPalette = [
  '#64748b',
  '#3b82f6',
  '#0ea5e9',
  '#14b8a6',
  '#22c55e',
  '#84cc16',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#ec4899',
  '#8b5cf6',
] as const;

/**
 * A person as shown in a project. In projects with `showInternalNames = false` external viewers
 * get internal people as `hidden` (E-08): no id, no name.
 */
export const personSchema = z.discriminatedUnion('hidden', [
  z.object({
    hidden: z.literal(false),
    id: z.uuid(),
    name: z.string(),
    userType: userTypeSchema,
    /**
     * Profile picture (ADM-07); null without one. Default for person snapshots stored before
     * profile pictures (notifications).
     */
    avatarUrl: z.string().nullable().default(null),
  }),
  z.object({ hidden: z.literal(true), userType: z.literal('internal') }),
]);
export type Person = z.infer<typeof personSchema>;

export const projectIdParamsSchema = z.object({ projectId: z.uuid() });

/** Problem `type` URIs of projects, so the UI can show specific messages. */
export const projectProblemTypes = {
  keyTaken: 'urn:pm-tool:problem:project-key-taken',
  invalidLead: 'urn:pm-tool:problem:project-invalid-lead',
  externalMembersPresent: 'urn:pm-tool:problem:project-external-members-present',
  notArchived: 'urn:pm-tool:problem:project-not-archived',
  statusNameTaken: 'urn:pm-tool:problem:status-name-taken',
  lastStatus: 'urn:pm-tool:problem:last-status',
  statusNotEmpty: 'urn:pm-tool:problem:status-not-empty',
  invalidTargetStatus: 'urn:pm-tool:problem:invalid-target-status',
  labelNameTaken: 'urn:pm-tool:problem:label-name-taken',
  lastManager: 'urn:pm-tool:problem:last-manager',
  leadMember: 'urn:pm-tool:problem:lead-member',
  externalForeignProject: 'urn:pm-tool:problem:external-foreign-project',
  noOrganization: 'urn:pm-tool:problem:project-without-organization',
} as const;

// --- Projects (PROJ-01, PROJ-02, PROJ-05) ------------------------------------------------------

export const projectKeySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,6}$/, '2–6 Großbuchstaben');

export const projectStatusSchema = z.enum(['planned', 'active', 'paused', 'completed']);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

export const scheduleModeSchema = z.enum(['auto', 'warn']);

export const projectNameSchema = z.string().trim().min(1).max(200);

export const projectSummarySchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  status: projectStatusSchema,
  organization: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  lead: personSchema.nullable(),
  startDate: isoDateSchema.nullable(),
  endDate: isoDateSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
  /** Top-level tasks (no milestones) the viewer can see; `done` = status category `done`. */
  progress: z.object({ done: z.number().int(), total: z.number().int() }),
  /** Earliest open milestone the viewer can see. */
  nextMilestone: z
    .object({ key: z.string(), title: z.string(), dueDate: isoDateSchema.nullable() })
    .nullable(),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

export const projectListQuerySchema = z.object({
  archived: z.stringbool().default(false),
  /** Only projects with own membership (DASH-02), e.g. without the observer access of project.view_all. */
  member: z.stringbool().default(false),
});

export const projectListResponseSchema = z.object({ items: z.array(projectSummarySchema) });
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;

export const projectSchema = projectSummarySchema.extend({
  description: richTextSchema.nullable(),
  /** Internal project settings: null for viewers without task.view_internal (all externals). */
  defaultTaskVisibility: visibilitySchema.nullable(),
  scheduleMode: scheduleModeSchema.nullable(),
  /** Holidays for scheduling (GANTT-07); null = company default (E-11) or external viewer. */
  holidayRegion: holidayRegionSchema.nullable(),
  /**
   * Region whose holidays apply (own region or company default), for every viewer like the
   * Gantt: weekend and holiday marks in the calendar (TASK-10).
   */
  effectiveHolidayRegion: holidayRegionSchema,
  showInternalNames: z.boolean().nullable(),
  createdAt: timestampSchema,
});
export type Project = z.infer<typeof projectSchema>;

function datesOrdered(v: {
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
}) {
  return v.startDate == null || v.endDate == null || v.startDate <= v.endDate;
}
const datesOrderedIssue = { message: 'Start date after end date', path: ['endDate'] };

export const createProjectBodySchema = z
  .object({
    key: projectKeySchema,
    name: projectNameSchema,
    description: richTextSchema.nullable().optional(),
    organizationId: z.uuid().nullable().optional(),
    /** Internal, active user; defaults to the creator. */
    leadUserId: z.uuid().optional(),
    status: projectStatusSchema.default('planned'),
    startDate: isoDateSchema.nullable().optional(),
    endDate: isoDateSchema.nullable().optional(),
    /** E-09: internal unless deliberately shared with the customer. */
    defaultTaskVisibility: visibilitySchema.default('internal'),
    showInternalNames: z.boolean().default(true),
  })
  .refine(datesOrdered, datesOrderedIssue);
export type CreateProjectBody = z.infer<typeof createProjectBodySchema>;

/** Date order is checked against the stored values in the service as well. */
export const updateProjectBodySchema = z
  .object({
    name: projectNameSchema.optional(),
    description: richTextSchema.nullable().optional(),
    organizationId: z.uuid().nullable().optional(),
    /** Must be an internal member of the project. */
    leadUserId: z.uuid().optional(),
    status: projectStatusSchema.optional(),
    startDate: isoDateSchema.nullable().optional(),
    endDate: isoDateSchema.nullable().optional(),
    defaultTaskVisibility: visibilitySchema.optional(),
    showInternalNames: z.boolean().optional(),
    /** GANTT-05 */
    scheduleMode: scheduleModeSchema.optional(),
    /** GANTT-07: null = company default (E-11). */
    holidayRegion: holidayRegionSchema.nullable().optional(),
  })
  .refine(datesOrdered, datesOrderedIssue);
export type UpdateProjectBody = z.infer<typeof updateProjectBodySchema>;

export const projectKeyParamsSchema = z.object({ key: projectKeySchema });

// --- Workflow (PROJ-03, PROJ-04) ---------------------------------------------------------------

export const statusCategorySchema = z.enum(['todo', 'in_progress', 'done']);
export type StatusCategory = z.infer<typeof statusCategorySchema>;

/** Standard workflow of new projects (PROJ-04), in this order. */
export const defaultWorkflow: readonly {
  name: string;
  category: StatusCategory;
  color: string;
}[] = [
  { name: 'Backlog', category: 'todo', color: '#64748b' },
  { name: 'To Do', category: 'todo', color: '#3b82f6' },
  { name: 'In Arbeit', category: 'in_progress', color: '#f59e0b' },
  { name: 'Review', category: 'in_progress', color: '#8b5cf6' },
  { name: 'Erledigt', category: 'done', color: '#22c55e' },
];

export const statusNameSchema = z.string().trim().min(1).max(50);
export const wipLimitSchema = z.number().int().min(1).max(999);

export const workflowStatusSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  category: statusCategorySchema,
  color: z.string(),
  position: z.string(),
  wipLimit: z.number().int().nullable(),
  /** Tasks in this status (not deleted), regardless of visibility; only for project.edit. */
  taskCount: z.number().int().nullable(),
});
export type WorkflowStatus = z.infer<typeof workflowStatusSchema>;

export const workflowStatusListResponseSchema = z.object({ items: z.array(workflowStatusSchema) });

export const createStatusBodySchema = z.object({
  name: statusNameSchema,
  category: statusCategorySchema,
  color: colorSchema,
  wipLimit: wipLimitSchema.nullable().optional(),
  /** Insert after this status; null = first, omitted = last. */
  afterStatusId: z.uuid().nullable().optional(),
});
export type CreateStatusBody = z.infer<typeof createStatusBodySchema>;

export const updateStatusBodySchema = z.object({
  name: statusNameSchema.optional(),
  category: statusCategorySchema.optional(),
  color: colorSchema.optional(),
  wipLimit: wipLimitSchema.nullable().optional(),
  /** Move after this status; null = first. */
  afterStatusId: z.uuid().nullable().optional(),
});
export type UpdateStatusBody = z.infer<typeof updateStatusBodySchema>;

export const statusParamsSchema = projectIdParamsSchema.extend({ statusId: z.uuid() });

/** Tasks in the deleted status move to `targetStatusId` (required if there are any). */
export const deleteStatusQuerySchema = z.object({ targetStatusId: z.uuid().optional() });

// --- Labels ------------------------------------------------------------------------------------

export const labelNameSchema = z.string().trim().min(1).max(50);

export const labelSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
});
export type Label = z.infer<typeof labelSchema>;

export const labelListResponseSchema = z.object({ items: z.array(labelSchema) });

export const createLabelBodySchema = z.object({ name: labelNameSchema, color: colorSchema });
export type CreateLabelBody = z.infer<typeof createLabelBodySchema>;

export const updateLabelBodySchema = z.object({
  name: labelNameSchema.optional(),
  color: colorSchema.optional(),
});
export type UpdateLabelBody = z.infer<typeof updateLabelBodySchema>;

export const labelParamsSchema = projectIdParamsSchema.extend({ labelId: z.uuid() });

// --- Members -----------------------------------------------------------------------------------

export const projectMemberSchema = z.object({
  user: personSchema,
  /** Only for viewers with project.manage_members. */
  email: z.string().nullable(),
  /** Customer organization of external members. */
  organizationName: z.string().nullable(),
  role: z.object({ id: z.uuid(), name: z.string() }),
  /** Invitation not accepted yet; only reported to member managers (false for everyone else). */
  invited: z.boolean(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

export const projectMemberListResponseSchema = z.object({ items: z.array(projectMemberSchema) });

export const memberParamsSchema = projectIdParamsSchema.extend({ userId: z.uuid() });

export const setMemberBodySchema = z.object({ roleId: z.uuid() });
export type SetMemberBody = z.infer<typeof setMemberBodySchema>;

export const memberCandidatesQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
});

/** Users who can join (internal, plus externals of the project organization) and project roles. */
export const memberCandidatesResponseSchema = z.object({
  users: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      email: z.string(),
      userType: userTypeSchema,
    }),
  ),
  roles: z.array(z.object({ id: z.uuid(), name: z.string(), externallyAssignable: z.boolean() })),
});
export type MemberCandidatesResponse = z.infer<typeof memberCandidatesResponseSchema>;

/** POST /projects/:projectId/invitations – invite a customer of the project organization (E-33). */
export const projectInvitationBodySchema = z.object({
  email: emailSchema,
  name: userNameSchema,
  roleId: z.uuid(),
});
export type ProjectInvitationBody = z.infer<typeof projectInvitationBodySchema>;

/**
 * `invited`: new account, `reinvited`: not accepted yet, new link (older links stop working),
 * `added`: an active customer of the organization joined directly (E-31 mail instead).
 * `link` only when the invitation e-mail could not be sent, to pass it on manually.
 */
export const projectInvitationResponseSchema = z.object({
  outcome: z.enum(['invited', 'reinvited', 'added']),
  member: projectMemberSchema,
  mailSent: z.boolean(),
  link: z.string().nullable(),
});
export type ProjectInvitationResponse = z.infer<typeof projectInvitationResponseSchema>;

// --- Options for pickers (internal users only) -------------------------------------------------

export const organizationOptionsResponseSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), name: z.string() })),
});

export const userOptionsQuerySchema = z.object({ q: z.string().trim().max(200).optional() });

export const userOptionsResponseSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), name: z.string(), email: z.string() })),
});
